package uz.mint.frontend.api

import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.SiteStrings
import uz.mint.frontend.i18n.plural

/**
 * The design's three tariff tabs.
 *
 * Only the tab name travels: **classification happens server-side** now that the catalogue is
 * paginated in the database. Filtering in Kotlin would mean downloading all ~2 900 plans to show
 * six of them, which is the thing pagination exists to avoid. The rule itself lives in
 * `PlanRepository.REGION_IS_GLOBAL`, alongside the query that applies it.
 */
enum class PlanCategory { LOCAL, REGIONAL, GLOBAL }

/**
 * Flag emoji built from the ISO alpha-2 code via regional-indicator code points.
 *
 * Beats the design's name→emoji lookup table: it covers every country the provider might add
 * without anyone maintaining a map. Regional and global bundles have no country, so they fall
 * back to a globe.
 */
fun Plan.flag(): String = flagFor(countryCode)

/**
 * The same rule for anything that has a country code — plan cards and destination cards alike.
 *
 * Shared so the flag beside "Турция" on the picker and the flag on the Turkish plan card can never
 * be different glyphs.
 */
fun flagFor(countryCode: String?): String {
    val cc = countryCode ?: return "🌐"
    if (cc.length != 2) return "🌐"
    val base = 0x1F1E6
    val a = base + (cc[0].uppercaseChar() - 'A')
    val b = base + (cc[1].uppercaseChar() - 'A')
    return buildString {
        appendCodePoint(a)
        appendCodePoint(b)
    }
}

/** Countries get their flag; multi-country bundles get the globe. */
fun Destination.flag(): String =
    if (kind == DestinationKind.COUNTRY) flagFor(id) else "🌐"

/** "от 20 328 сум", or the no-rate stand-in — the same rule the plan cards follow. */
fun Destination.priceLabel(strings: SiteStrings): String =
    fromPriceUzs?.let { formatUzs(it, strings) } ?: strings.priceUnavailable

private fun StringBuilder.appendCodePoint(cp: Int) {
    val v = cp - 0x10000
    append(((v shr 10) + 0xD800).toChar())
    append(((v and 0x3FF) + 0xDC00).toChar())
}

/**
 * The one price the storefront shows.
 *
 * The catalogue is priced in USD — that is what the supplier's CSV carries — but a customer in
 * Uzbekistan pays in so'm and Payme settles in so'm, so a dollar figure beside a pay button is
 * never the number that gets charged. The conversion happens server-side ([Plan.priceUzs], from
 * the admin's FX rate), so this only formats it.
 *
 * With no rate configured there is no sum to print, and falling back to dollars would put the
 * currency back on the page it was taken off. [SiteStrings.priceUnavailable] says so instead.
 */
fun Plan.priceLabel(strings: SiteStrings): String =
    priceUzs?.let { formatUzs(it, strings) } ?: strings.priceUnavailable

/** "158 125 сум". Also used for the exact sum Payme is about to charge. */
fun formatUzs(amount: Double, strings: SiteStrings): String =
    groupThousands(amount) + " " + strings.currencyUzs

private fun groupThousands(amount: Double): String {
    val digits = kotlin.math.round(amount).toLong().toString()
    return buildString {
        digits.forEachIndexed { index, char ->
            if (index > 0 && (digits.length - index) % 3 == 0) append(' ')
            append(char)
        }
    }
}

/** "10 ГБ" / "512 МБ" — whole gigabytes read better than four-digit megabytes. */
fun Plan.dataLabel(strings: SiteStrings): String =
    if (dataAmountMb % 1024 == 0) "${dataAmountMb / 1024} ${strings.unitGb}"
    else "$dataAmountMb ${strings.unitMb}"

/** "30 дней" — Russian numeral agreement matters on the locale most visitors land on. */
fun Plan.validityLabel(locale: Locale, strings: SiteStrings): String =
    "$validityDays " + locale.plural(validityDays, strings.dayForms)

/** The card's small-caps tag: "10 ГБ / 30 дней". */
fun Plan.tag(locale: Locale, strings: SiteStrings): String =
    dataLabel(strings) + " / " + validityLabel(locale, strings)
